import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';

const FACTORS = [
  {
    key: 'payment',
    label: 'Payment History',
    weight: 35,
    icon: '✅',
    color: '#22c55e',
    description: 'The single most important factor. Every on-time payment helps. Every missed payment hurts — and stays on your report for 7 years.',
    tips: [
      'Set up autopay for at least the minimum payment on every account',
      'If you missed a payment, catch up immediately — the damage grows the longer it\'s late',
      'One missed payment can drop your score 50-100 points',
      'A perfect payment history for 12+ months significantly boosts your score',
    ],
  },
  {
    key: 'utilization',
    label: 'Credit Utilization',
    weight: 30,
    icon: '💳',
    color: '#38bdf8',
    description: 'How much of your available credit you\'re using. Lower is better. Aim for under 30% — ideally under 10%.',
    tips: [
      'If you have a $10,000 credit limit, keep your balance under $3,000 (30%) — ideally under $1,000 (10%)',
      'Pay your balance in full every month to keep utilization low',
      'Ask for a credit limit increase (without spending more) to improve your ratio instantly',
      'Spread balances across cards rather than maxing one out',
    ],
  },
  {
    key: 'length',
    label: 'Length of Credit History',
    weight: 15,
    icon: '📅',
    color: '#a78bfa',
    description: 'How long you\'ve had credit. Older accounts help. Don\'t close your oldest credit card.',
    tips: [
      'Keep your oldest credit card open even if you don\'t use it (set a small recurring charge)',
      'Closing an old card lowers your average account age and removes available credit',
      'Be patient — this factor improves automatically over time',
      'If you\'re new to credit, a secured credit card or credit-builder loan starts your history',
    ],
  },
  {
    key: 'mix',
    label: 'Credit Mix',
    weight: 10,
    icon: '🔀',
    color: '#f59e0b',
    description: 'Having different types of credit (cards, loans, mortgage) shows lenders you can manage multiple accounts.',
    tips: [
      'Having both revolving credit (cards) and installment loans (car, student, personal) helps',
      'Don\'t open new accounts just for the mix — it\'s a minor factor',
      'If you only have credit cards, a small credit-builder loan can help',
    ],
  },
  {
    key: 'newCredit',
    label: 'New Credit / Hard Inquiries',
    weight: 10,
    icon: '🔍',
    color: '#ef4444',
    description: 'Every time you apply for credit, a hard inquiry appears. Too many in a short period signals risk.',
    tips: [
      'Each hard inquiry typically drops your score 5-10 points for up to 12 months',
      'Rate shopping for mortgages or car loans within 14-45 days counts as one inquiry',
      'Checking your own credit score is a soft inquiry — it does NOT affect your score',
      'Avoid applying for new cards in the 6-12 months before a major loan (mortgage, car)',
    ],
  },
];

const SCORE_RANGES = [
  { min: 800, max: 850, label: 'Exceptional', color: '#22c55e', desc: 'Best rates on everything. You\'re in the top tier.' },
  { min: 740, max: 799, label: 'Very Good',   color: '#86efac', desc: 'Above average. You qualify for most loans at competitive rates.' },
  { min: 670, max: 739, label: 'Good',         color: '#f59e0b', desc: 'Near or above average. Most lenders will approve you.' },
  { min: 580, max: 669, label: 'Fair',          color: '#f97316', desc: 'Below average. Higher rates. Some lenders may decline.' },
  { min: 300, max: 579, label: 'Poor',          color: '#ef4444', desc: 'Significant challenges. Focus on rebuilding before major applications.' },
];

const ACTION_PLAN = [
  { range: [300, 579], title: 'Building from scratch', steps: ['Get a secured credit card (deposit becomes your limit — Capital One Secured, Discover Secured)', 'Set up autopay immediately', 'Keep utilization under 30%', 'Check your credit report for errors at AnnualCreditReport.com (free)', 'Be patient — with consistent habits, expect +50-100 points in 12 months'] },
  { range: [580, 669], title: 'Moving from Fair to Good', steps: ['Pay every bill on time — no exceptions', 'Pay down balances to get utilization under 30%', 'Dispute any errors on your credit report', 'Don\'t close old accounts or open new ones', 'With focus, expect +40-80 points in 6-12 months'] },
  { range: [670, 739], title: 'Improving your Good score', steps: ['Get utilization under 10% on all cards', 'Continue perfect payment history', 'Let your account age grow — don\'t close cards', 'Consider a credit limit increase request (soft pull)', 'Expect +30-60 points in 6-12 months of discipline'] },
  { range: [740, 799], title: 'Reaching Exceptional', steps: ['Get utilization to single digits (under 5% is ideal)', 'Maintain spotless payment history', 'Limit new credit applications', 'Let your existing accounts age', 'The jump to 800+ is mostly time + zero mistakes'] },
  { range: [800, 850], title: 'Maintaining Exceptional', steps: ['Keep doing what you\'re doing', 'Pay in full every month', 'Keep old accounts open', 'Monitor for errors or fraud regularly', 'You\'ll get the best rates on any loan you apply for'] },
];

function ScoreInput() {
  const [score, setScore] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const num = parseInt(score);
  const range = SCORE_RANGES.find(r => num >= r.min && num <= r.max);
  const plan = ACTION_PLAN.find(p => num >= p.range[0] && num <= p.range[1]);

  return (
    <div style={s.scoreInputSection}>
      <h2 style={s.h2}>What\'s Your Score?</h2>
      <p style={s.p}>Enter your current credit score and get a personalized improvement roadmap.</p>
      <div style={s.inputRow}>
        <input
          style={s.scoreInput}
          type="number"
          min="300"
          max="850"
          placeholder="e.g. 680"
          value={score}
          onChange={e => { setScore(e.target.value); setSubmitted(false); }}
        />
        <button
          style={{ ...s.scoreBtn, ...((!num || num < 300 || num > 850) ? s.scoreBtnDisabled : {}) }}
          disabled={!num || num < 300 || num > 850}
          onClick={() => setSubmitted(true)}
        >
          Get My Plan
        </button>
      </div>
      <p style={s.scoreHint}>Don\'t know your score? Check for free at your bank\'s app, Credit Karma, or Experian.com.</p>

      {submitted && range && plan && (
        <div style={s.scoreResult}>
          <div style={{ ...s.scoreBadge, background: range.color + '18', color: range.color, border: `1px solid ${range.color}33` }}>
            {num} — {range.label}
          </div>
          <p style={s.scoreDesc}>{range.desc}</p>
          <div style={s.planBox}>
            <div style={s.planTitle}>Your Action Plan: {plan.title}</div>
            <ul style={s.planList}>
              {plan.steps.map((step, i) => (
                <li key={i} style={s.planItem}>
                  <span style={s.planNum}>{i + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CreditScoreGuide() {
  const [openFactor, setOpenFactor] = useState(null);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: 'What is a good credit score?', acceptedAnswer: { '@type': 'Answer', text: 'A credit score of 670-739 is considered "Good," 740-799 is "Very Good," and 800-850 is "Exceptional." Most lenders approve borrowers with scores above 670. The best interest rates go to borrowers with scores of 740 or higher.' } },
      { '@type': 'Question', name: 'How long does it take to improve your credit score?', acceptedAnswer: { '@type': 'Answer', text: 'You can see improvements in 1-3 months by paying down balances and clearing late payments. Significant improvements (50-100 points) typically take 6-12 months of consistent on-time payments and low utilization. Building an excellent score from scratch takes 2-3 years.' } },
      { '@type': 'Question', name: 'What hurts your credit score the most?', acceptedAnswer: { '@type': 'Answer', text: 'The biggest damage comes from missed or late payments (payment history is 35% of your score). A single 30-day late payment can drop your score 50-100 points. High credit utilization (above 30%) is the second biggest negative factor.' } },
    ],
  };

  return (
    <div style={s.page}>
      <SEO
        title="How to Improve Your Credit Score — Step-by-Step Guide"
        description="The complete guide to improving your credit score. Learn what affects it, how each factor works, and get a personalized action plan based on where you are now."
        path="/credit-score-guide"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div style={s.glow} />
      <div style={s.inner}>

        {/* Header */}
        <div style={s.header}>
          <div style={s.badge}>📖 Free Guide</div>
          <h1 style={s.h1}>How to Improve Your Credit Score</h1>
          <p style={s.subtitle}>What affects your score, how much each factor matters, and exactly what to do — based on where you are now.</p>
        </div>

        {/* Score ranges */}
        <div style={s.section}>
          <h2 style={s.h2}>Credit Score Ranges</h2>
          <div style={s.rangesGrid}>
            {SCORE_RANGES.map(r => (
              <div key={r.label} style={{ ...s.rangeCard, borderColor: r.color + '33' }}>
                <div style={{ ...s.rangeLabel, color: r.color }}>{r.label}</div>
                <div style={s.rangeNums}>{r.min}–{r.max}</div>
                <div style={s.rangeDesc}>{r.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Interactive score input */}
        <ScoreInput />

        {/* The 5 factors */}
        <div style={s.section}>
          <h2 style={s.h2}>The 5 Factors That Make Up Your Score</h2>
          <p style={s.p}>Your FICO score is calculated from five ingredients. Here\'s how much each one matters — and exactly how to improve it.</p>

          <div style={s.factorsGrid}>
            {FACTORS.map(f => (
              <div key={f.key} style={s.factorCard}>
                <div style={s.factorTop} onClick={() => setOpenFactor(openFactor === f.key ? null : f.key)}>
                  <div style={s.factorLeft}>
                    <span style={s.factorIcon}>{f.icon}</span>
                    <div>
                      <div style={s.factorLabel}>{f.label}</div>
                      <div style={s.factorDesc}>{f.description}</div>
                    </div>
                  </div>
                  <div style={s.factorRight}>
                    <div style={{ ...s.factorWeight, background: f.color + '18', color: f.color, border: `1px solid ${f.color}33` }}>{f.weight}%</div>
                    <span style={s.factorToggle}>{openFactor === f.key ? '▲' : '▼'}</span>
                  </div>
                </div>

                {/* Progress bar */}
                <div style={s.barTrack}>
                  <div style={{ ...s.barFill, width: `${f.weight * 2.86}%`, background: f.color }} />
                </div>

                {/* Expanded tips */}
                {openFactor === f.key && (
                  <div style={s.tipsBox}>
                    <div style={s.tipsTitle}>How to improve this factor:</div>
                    <ul style={s.tipsList}>
                      {f.tips.map((tip, i) => (
                        <li key={i} style={s.tipItem}>{tip}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Quick wins */}
        <div style={s.quickWins}>
          <h2 style={{ ...s.h2, margin: '0 0 20px' }}>Quick Wins (Can See Results in 30-60 Days)</h2>
          <div style={s.winsGrid}>
            {[
              { icon: '⚡', title: 'Pay down a credit card balance', desc: 'Reducing utilization below 30% can add 20-50 points quickly. Under 10% is even better.' },
              { icon: '🚨', title: 'Dispute errors on your report', desc: 'Get your free report at AnnualCreditReport.com. Errors affect 1 in 5 reports. Dispute online at Equifax, Experian, TransUnion.' },
              { icon: '📈', title: 'Request a credit limit increase', desc: 'If you haven\'t missed payments, your card issuer will often raise your limit. Same balance + higher limit = lower utilization.' },
              { icon: '🔒', title: 'Set up autopay', desc: 'The simplest move. One missed payment wipes out months of progress. Set autopay for at least the minimum on every account.' },
            ].map((w, i) => (
              <div key={i} style={s.winCard}>
                <div style={s.winIcon}>{w.icon}</div>
                <div style={s.winTitle}>{w.title}</div>
                <div style={s.winDesc}>{w.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div style={s.section}>
          <h2 style={s.h2}>Frequently Asked Questions</h2>
          {[
            { q: 'What is a good credit score?', a: 'A score of 670-739 is "Good," 740-799 is "Very Good," and 800-850 is "Exceptional." Most lenders approve above 670. The best interest rates (on mortgages, car loans, etc.) go to borrowers at 740 or higher.' },
            { q: 'How long does it take to improve your credit score?', a: 'You can see improvements in 1-3 months by paying down balances. Significant jumps (50-100 points) take 6-12 months of consistent on-time payments and low utilization. Building from scratch to a good score takes 1-2 years.' },
            { q: 'Does checking your own credit score hurt it?', a: 'No. Checking your own score is a "soft inquiry" and has zero impact on your score. Only "hard inquiries" (when a lender checks your credit for a loan application) affect your score — and only by 5-10 points for up to 12 months.' },
            { q: 'What hurts your credit score the most?', a: 'Missed or late payments (35% of your score). A single 30-day late payment can drop your score 50-100 points and stays on your report for 7 years. High credit utilization above 30% is the second biggest negative factor.' },
            { q: 'How do I build credit from scratch?', a: 'Start with a secured credit card (you deposit $200-500 as collateral, it becomes your limit). Use it for small purchases, pay it in full every month, and set up autopay. After 6-12 months of on-time payments, you\'ll have a credit history and can apply for a regular card.' },
          ].map((item, i) => (
            <div key={i} style={s.faqItem}>
              <div style={s.faqQ}>Q: {item.q}</div>
              <div style={s.faqA}>{item.a}</div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div style={s.ctaSection}>
          <div style={s.ctaText}>
            <div style={s.ctaTitle}>Track your full financial health — not just credit</div>
            <div style={s.ctaDesc}>KnowFi\'s Health Score gives you a complete picture: savings, debt, income, investments, and emergency fund in one score.</div>
          </div>
          <Link to="/healthscore" style={s.ctaBtn}>Check My Health Score →</Link>
        </div>

        {/* Disclaimer */}
        <p style={s.disclaimer}>
          <strong>Disclaimer:</strong> This guide is for educational purposes only and is not financial or credit counseling advice. Credit scoring models vary. Results depend on your individual credit history and lender criteria. Always verify current information with your creditors and credit bureaus.
        </p>

        <div style={s.backRow}>
          <Link to="/howto" style={s.backLink}>← All How-To Guides</Link>
          <Link to="/glossary/credit-score" style={s.glossLink}>What is a credit score? →</Link>
        </div>

      </div>
    </div>
  );
}

const s = {
  page:  { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', padding: '60px 24px 80px', position: 'relative', overflow: 'hidden' },
  glow:  { position: 'absolute', top: '-80px', left: '50%', transform: 'translateX(-50%)', width: '700px', height: '500px', background: 'radial-gradient(ellipse at center, rgba(251,146,60,0.06) 0%, transparent 65%)', pointerEvents: 'none' },
  inner: { maxWidth: '860px', margin: '0 auto', position: 'relative', zIndex: 1 },

  header:   { textAlign: 'center', marginBottom: '48px' },
  badge:    { display: 'inline-block', background: 'rgba(251,146,60,0.1)', color: '#fb923c', border: '1px solid rgba(251,146,60,0.25)', borderRadius: '20px', padding: '4px 14px', fontSize: '12px', fontWeight: '700', marginBottom: '16px', letterSpacing: '0.3px' },
  h1:       { fontSize: 'clamp(26px, 4vw, 42px)', fontWeight: '900', margin: '0 0 14px', letterSpacing: '-1px', lineHeight: 1.2, color: '#f1f5f9' },
  subtitle: { color: '#64748b', fontSize: '17px', maxWidth: '600px', margin: '0 auto', lineHeight: 1.6 },

  section: { marginBottom: '48px' },
  h2:      { fontSize: '22px', fontWeight: '800', color: '#f1f5f9', margin: '0 0 16px', letterSpacing: '-0.3px' },
  p:       { color: '#94a3b8', fontSize: '15px', lineHeight: 1.7, margin: '0 0 20px' },

  // Score ranges
  rangesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' },
  rangeCard:  { background: '#1e293b', borderRadius: '12px', padding: '16px', border: '1px solid #334155', textAlign: 'center' },
  rangeLabel: { fontSize: '13px', fontWeight: '800', marginBottom: '4px' },
  rangeNums:  { fontSize: '18px', fontWeight: '900', color: '#f1f5f9', marginBottom: '6px' },
  rangeDesc:  { fontSize: '11px', color: '#64748b', lineHeight: 1.45 },

  // Score input
  scoreInputSection: { background: '#1e293b', borderRadius: '16px', padding: '32px', border: '1px solid #334155', marginBottom: '48px' },
  inputRow:   { display: 'flex', gap: '12px', marginBottom: '10px', flexWrap: 'wrap' },
  scoreInput: { background: '#0f172a', border: '1px solid #334155', borderRadius: '10px', color: '#f8fafc', padding: '12px 16px', fontSize: '18px', fontFamily: 'inherit', outline: 'none', width: '160px', fontWeight: '700' },
  scoreBtn:   { background: '#fb923c', color: '#0f172a', border: 'none', padding: '12px 24px', borderRadius: '10px', fontWeight: '800', fontSize: '15px', cursor: 'pointer', fontFamily: 'inherit' },
  scoreBtnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  scoreHint:  { color: '#475569', fontSize: '12px', margin: 0 },
  scoreResult:{ marginTop: '24px' },
  scoreBadge: { display: 'inline-block', fontSize: '16px', fontWeight: '800', padding: '8px 18px', borderRadius: '20px', marginBottom: '12px' },
  scoreDesc:  { color: '#94a3b8', fontSize: '15px', marginBottom: '20px' },
  planBox:    { background: '#0f172a', borderRadius: '12px', padding: '20px 24px', border: '1px solid rgba(251,146,60,0.2)' },
  planTitle:  { fontSize: '15px', fontWeight: '800', color: '#fb923c', marginBottom: '14px' },
  planList:   { margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '10px' },
  planItem:   { display: 'flex', gap: '12px', alignItems: 'flex-start', fontSize: '14px', color: '#94a3b8', lineHeight: 1.55 },
  planNum:    { background: 'rgba(251,146,60,0.15)', color: '#fb923c', borderRadius: '50%', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '800', flexShrink: 0, marginTop: '1px' },

  // Factors
  factorsGrid: { display: 'flex', flexDirection: 'column', gap: '12px' },
  factorCard:  { background: '#1e293b', borderRadius: '14px', padding: '20px', border: '1px solid #334155', cursor: 'pointer' },
  factorTop:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' },
  factorLeft:  { display: 'flex', gap: '14px', alignItems: 'flex-start', flex: 1 },
  factorIcon:  { fontSize: '22px', flexShrink: 0, marginTop: '2px' },
  factorLabel: { fontSize: '15px', fontWeight: '700', color: '#f1f5f9', marginBottom: '4px' },
  factorDesc:  { fontSize: '13px', color: '#64748b', lineHeight: 1.5 },
  factorRight: { display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 },
  factorWeight:{ fontSize: '13px', fontWeight: '800', padding: '4px 12px', borderRadius: '20px' },
  factorToggle:{ color: '#475569', fontSize: '12px' },
  barTrack:    { height: '4px', background: '#0f172a', borderRadius: '4px', overflow: 'hidden' },
  barFill:     { height: '100%', borderRadius: '4px', transition: 'width 0.5s ease' },
  tipsBox:     { marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #334155' },
  tipsTitle:   { fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' },
  tipsList:    { margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px' },
  tipItem:     { fontSize: '13px', color: '#94a3b8', lineHeight: 1.55, paddingLeft: '14px', borderLeft: '2px solid #334155' },

  // Quick wins
  quickWins:  { background: 'rgba(34,197,94,0.04)', border: '1px solid rgba(34,197,94,0.15)', borderRadius: '16px', padding: '28px', marginBottom: '48px' },
  winsGrid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' },
  winCard:    { background: '#1e293b', borderRadius: '12px', padding: '18px', border: '1px solid #334155' },
  winIcon:    { fontSize: '24px', marginBottom: '10px' },
  winTitle:   { fontSize: '14px', fontWeight: '700', color: '#f1f5f9', marginBottom: '6px' },
  winDesc:    { fontSize: '13px', color: '#64748b', lineHeight: 1.5 },

  // FAQ
  faqItem: { background: 'rgba(99,102,241,0.05)', border: '1px solid rgba(99,102,241,0.15)', borderRadius: '12px', padding: '16px 20px', marginBottom: '10px' },
  faqQ:    { fontSize: '14px', fontWeight: '700', color: '#f1f5f9', marginBottom: '8px' },
  faqA:    { fontSize: '13px', color: '#94a3b8', lineHeight: 1.65 },

  // CTA
  ctaSection: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px', background: '#1e293b', borderRadius: '16px', padding: '28px 32px', border: '1px solid #334155', marginBottom: '32px' },
  ctaText:    {},
  ctaTitle:   { fontSize: '17px', fontWeight: '800', color: '#f1f5f9', marginBottom: '6px' },
  ctaDesc:    { fontSize: '13px', color: '#64748b', lineHeight: 1.5, maxWidth: '480px' },
  ctaBtn:     { background: '#fb923c', color: '#0f172a', padding: '12px 22px', borderRadius: '10px', fontWeight: '800', fontSize: '14px', textDecoration: 'none', whiteSpace: 'nowrap' },

  disclaimer: { color: '#475569', fontSize: '12px', lineHeight: 1.6, padding: '14px 18px', background: 'rgba(100,116,139,0.06)', border: '1px solid #1e293b', borderRadius: '10px', marginBottom: '24px' },
  backRow:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  backLink:   { color: '#475569', textDecoration: 'none', fontSize: '13px' },
  glossLink:  { color: '#38bdf8', textDecoration: 'none', fontSize: '13px', fontWeight: '600' },
};
